import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  organizationId?: string;
  role: string;
  isOrgOwner: boolean;
  emailVerified: boolean;
  preferences: any;
  metrics: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  avatarUrl?: string;
  branches?: any[]; // Branch assignments from backend
  primaryBranch?: any; // Primary branch assignment
}

interface Organization {
  id: string;
  name: string;
  slug?: string;
  logoUrl?: string;
  country?: string;
  industry?: string;
  currency?: string;
  timezone?: string;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const savedToken = localStorage.getItem('leadshive_token');
        const savedUser = localStorage.getItem('leadshive_user');
        const savedOrganization = localStorage.getItem('leadshive_organization');

        if (savedToken && savedUser) {
          setToken(savedToken);
          const parsedUser = JSON.parse(savedUser);
          // Don't restore avatarUrl from localStorage — it may be an expired signed URL.
          // Verify will set a fresh avatarUrl after token check.
          delete (parsedUser as { avatarUrl?: string }).avatarUrl;
          setUser(parsedUser);
          
          if (savedOrganization) {
            setOrganization(JSON.parse(savedOrganization));
          }
          
          // Verify token is still valid
          try {
            const response = await authAPI.verify();
            if (response.success) {
              setUser(response.user);
              localStorage.setItem('leadshive_user', JSON.stringify(response.user));
            }
          } catch (error) {
            // Token is invalid, clear storage
            localStorage.removeItem('leadshive_token');
            localStorage.removeItem('leadshive_user');
            localStorage.removeItem('leadshive_organization');
            setToken(null);
            setUser(null);
            setOrganization(null);
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login(email, password);
      
      if (response.success) {
        setToken(response.token);
        setUser(response.user);
        
        // Save to localStorage
        localStorage.setItem('leadshive_token', response.token);
        localStorage.setItem('leadshive_user', JSON.stringify(response.user));
        
        // Save organization data if available
        if (response.organization) {
          setOrganization(response.organization);
          localStorage.setItem('leadshive_organization', JSON.stringify(response.organization));
        }
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setOrganization(null);
    setToken(null);
    localStorage.removeItem('leadshive_token');
    localStorage.removeItem('leadshive_user');
    localStorage.removeItem('leadshive_organization');
    
    // Call logout API
    authAPI.logout().catch(console.error);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('leadshive_user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    organization,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
